@if (isLoading()) {
    <div class="flex min-h-[8rem] items-center justify-center py-6">
        <p-progressSpinner class="h-10 w-10 text-primary dark:text-gray-300" />
    </div>
} @else if (loadFailed()) {
    <p class="px-4 py-6 text-sm text-red-600 dark:text-red-400">
        {{ loadFailedMessageKey | translate }}
    </p>
} @else if (isEmpty()) {
    <p class="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
        {{ emptyMessageKey | translate }}
    </p>
} @else {
    <div class="flex min-h-0 flex-1 flex-col gap-4 pr-2 pb-4">
        <div class="grid grid-cols-2 gap-2.5 xl:grid-cols-3">
            <app-phx-stat-card
                [fillHeight]="true"
                color="sky"
                [label]="'document-line-item.price-history.avg-unit-price' | translate"
                [value]="statAvgUnitPrice()"
                [showSkeletonWhenEmpty]="false"
                icon="tag"
                dataTestId="price-history-stat-avg-unit-price"
            />
            <app-phx-stat-card
                [fillHeight]="true"
                color="orange"
                [label]="'document-line-item.price-history.peak-unit-price' | translate"
                [value]="statPeakUnitPrice()"
                [showSkeletonWhenEmpty]="false"
                icon="arrow-up"
                dataTestId="price-history-stat-peak-price"
            />
            <app-phx-stat-card
                [fillHeight]="true"
                [color]="statCurrentDeltaColor()"
                [label]="'document-line-item.price-history.current-delta' | translate"
                [value]="statCurrentDelta()"
                [badge]="statCurrentDeltaBadge()"
                [showSkeletonWhenEmpty]="false"
                icon="scale-balanced"
                dataTestId="price-history-stat-current-delta"
            />
        </div>

        @if (chartData(); as data) {
            <div
                class="h-60 shrink-0 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/40"
                [attr.data-testid]="'line-item-price-history-chart'"
            >
                @defer (on immediate) {
                    <phx-chart type="line" height="100%" [data]="data" [options]="chartOptions()" />
                } @placeholder {
                    <div class="flex h-full items-center justify-center">
                        <p-progressSpinner class="h-10 w-10 text-primary dark:text-gray-300" />
                    </div>
                }
            </div>
        }

        <p-table
            [value]="items()"
            [scrollable]="true"
            scrollHeight="flex"
            styleClass="p-datatable-sm"
            [attr.data-testid]="'line-item-price-history-table'"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th>{{ 'document-line-item.price-history.date' | translate }}</th>
                    <th>{{ 'document-line-item.price-history.document' | translate }}</th>
                    <th class="!text-right">
                        {{ 'document-line-item.price-history.quantity' | translate }}
                    </th>
                    <th class="!text-right">
                        <span
                            class="block w-full text-right"
                            [pTooltip]="applyPriceTooltipKey | translate"
                            tooltipPosition="top"
                        >
                            {{ 'document-line-item.price-history.unit-price' | translate }}
                        </span>
                    </th>
                    <th class="!text-right">
                        {{ 'document-line-item.calculatory-costs' | translate }}
                    </th>
                    <th class="!text-right">
                        {{ 'document-line-item.price-history.discount' | translate }}
                    </th>
                    <th class="!text-right">
                        {{ 'document-line-item.price-history.delta' | translate }}
                    </th>
                    <th class="!text-right">
                        {{ 'document-line-item.price-history.line-total' | translate }}
                    </th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>{{ row.date | localeDate: 'shortDateCompact' }}</td>
                    <td>
                        @if (row.documentId) {
                            <a
                                class="text-primary hover:underline"
                                [routerLink]="['/documents/edit', row.documentId]"
                            >
                                {{ row.documentIdentifier ?? row.documentId }}
                            </a>
                            @if (showMixedDocumentTypes() && row.documentType) {
                                <span class="ml-1 text-xs text-slate-500"
                                    >({{ row.documentType }})</span
                                >
                            }
                        } @else {
                            {{ row.documentIdentifier ?? '—' }}
                        }
                    </td>
                    <td class="!text-right tabular-nums">{{ row.quantity ?? '—' }}</td>
                    <td class="!text-right tabular-nums">
                        @if (row.unitPrice != null) {
                            <div class="flex items-center justify-end gap-1.5">
                                @if (
                                    row.priceSource && priceSourceIcon(row.priceSource);
                                    as iconClass
                                ) {
                                    <span
                                        class="inline-flex shrink-0 items-center"
                                        [class]="unitPriceTextClass(row.priceSource, false)"
                                        [pTooltip]="priceSourceLabel(row.priceSource)"
                                        tooltipPosition="top"
                                        [attr.aria-label]="priceSourceLabel(row.priceSource)"
                                    >
                                        <i [class]="iconClass + ' text-xs'" aria-hidden="true"></i>
                                    </span>
                                }
                                <button
                                    type="button"
                                    [class]="
                                        unitPriceButtonClass(row.priceSource, applyPriceEnabled())
                                    "
                                    [disabled]="!applyPriceEnabled()"
                                    [pTooltip]="
                                        applyPriceEnabled()
                                            ? (applyPriceTooltipKey | translate)
                                            : (applyPriceDisabledTooltipKey | translate)
                                    "
                                    tooltipPosition="left"
                                    (click)="onUnitPriceClick(row)"
                                    [attr.data-testid]="'price-history-apply-unit-price'"
                                >
                                    {{ row.unitPrice | localeCurrency: row.currency }}
                                </button>
                            </div>
                        } @else {
                            —
                        }
                    </td>
                    <td class="!text-right tabular-nums">
                        @if (row.calculatoryCosts != null) {
                            {{ row.calculatoryCosts | localeCurrency: row.currency }}
                        } @else {
                            —
                        }
                    </td>
                    <td class="!text-right tabular-nums">
                        @if (
                            formatRowDiscount(row.discountPercentage, row.discountPercentage2);
                            as discountValue
                        ) {
                            {{ discountValue }}
                        } @else {
                            —
                        }
                    </td>
                    <td class="!text-right tabular-nums">
                        @if (formatRowDeltaPercent(row.deltaPercent); as deltaValue) {
                            <div class="flex justify-end">
                                <span
                                    class="inline-flex items-center gap-0.5 font-medium"
                                    [class]="rowDeltaClass(row.deltaPercent)"
                                >
                                    @switch (rowDeltaTrend(row.deltaPercent)) {
                                        @case ('up') {
                                            <i
                                                class="fa-solid fa-caret-up text-xs leading-none"
                                                aria-hidden="true"
                                            ></i>
                                        }
                                        @case ('down') {
                                            <i
                                                class="fa-solid fa-caret-down text-xs leading-none"
                                                aria-hidden="true"
                                            ></i>
                                        }
                                    }
                                    <span>{{ deltaValue }}</span>
                                </span>
                            </div>
                        } @else {
                            —
                        }
                    </td>
                    <td class="!text-right tabular-nums">
                        @if (row.lineTotal != null) {
                            {{ row.lineTotal | localeCurrency: row.currency }}
                        } @else {
                            —
                        }
                    </td>
                </tr>
            </ng-template>
        </p-table>

        <div
            class="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400"
            [attr.data-testid]="'price-history-price-source-legend'"
        >
            <span class="font-medium text-slate-600 dark:text-slate-300">
                {{ 'document-line-item.price-history.price-source-legend' | translate }}:
            </span>
            <span class="inline-flex items-center gap-1">
                <span
                    class="inline-flex h-5 w-5 items-center justify-center rounded ring-1 bg-orange-100 text-orange-700 ring-orange-200/80 dark:bg-orange-950/50 dark:text-orange-300 dark:ring-orange-800/60"
                >
                    <i class="fa-solid fa-pen text-[10px]" aria-hidden="true"></i>
                </span>
                {{ 'document-line-item.price-history.price-source-manual' | translate }}
            </span>
            <span class="inline-flex items-center gap-1">
                <span
                    class="inline-flex h-5 w-5 items-center justify-center rounded ring-1 bg-red-100 text-red-700 ring-red-200/80 dark:bg-red-950/50 dark:text-red-300 dark:ring-red-800/60"
                >
                    <i class="fa-solid fa-tags text-[10px]" aria-hidden="true"></i>
                </span>
                {{ 'document-line-item.price-history.price-source-price-list' | translate }}
            </span>
            <span class="inline-flex items-center gap-1">
                <span
                    class="inline-flex h-5 w-5 items-center justify-center rounded ring-1 bg-amber-100 text-amber-800 ring-amber-200/80 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800/60"
                >
                    <i class="fa-solid fa-user-tag text-[10px]" aria-hidden="true"></i>
                </span>
                {{ 'document-line-item.price-history.price-source-customer-price' | translate }}
            </span>
        </div>
    </div>
}
